// Utility helpers for formatting that must be consistent across the app.
// Important rules:
//  - Artwork titles, prices, and currency codes are NEVER translated.
//    Wrap them with the `<NoTranslate>` component or apply `translate="no"`.
//  - Dimensions are stored in centimetres in the database. When the UI
//    language is English we render them in inches.

import React from 'react';

const CM_TO_IN = 0.3937007874;

const formatInches = (cm) => {
  const inches = cm * CM_TO_IN;
  // Round to the nearest integer for a clean reading
  return `${Math.round(inches)}`;
};

/**
 * Convert any dimension string from centimetres to inches when needed.
 * Examples handled:
 *   "120 x 100 cm"        -> "47 1/4 x 39 3/8 in" (we keep decimals for simplicity)
 *   "120x100x40 cm"
 *   "Ø 50 cm"
 *   "50 cm"
 *
 * If the language is not English the original string is returned unchanged.
 * If the string does not contain numeric values we return it untouched.
 */
export const formatDimensions = (raw, lang) => {
  if (!raw || typeof raw !== 'string') return raw;
  if (lang !== 'en') return raw;

  // Match all numbers (integers or decimals, dot or comma) in the string.
  const numberRegex = /\d+(?:[.,]\d+)?/g;
  if (!numberRegex.test(raw)) return raw;

  // Reset regex and replace each number by its inch value.
  let result = raw.replace(/\d+(?:[.,]\d+)?/g, (match) => {
    const cm = parseFloat(match.replace(',', '.'));
    if (Number.isNaN(cm)) return match;
    return formatInches(cm);
  });

  // Replace the unit. Cover common variants (cm, cms, centimetres/centimeters).
  result = result.replace(/\bcms?\b\.?/gi, 'in');
  result = result.replace(/cent[ií]met(re|er|ro)s?/gi, 'inches');

  return result;
};

/** Format a USD price. Currency code is intentionally a separate node so it
 *  is easy to mark as non-translatable. */
export const formatUSD = (price) => {
  const value = Number(price || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return `$${value}`;
};

/** Inline span that prevents browser auto-translation. */
export const NoTranslate = ({ children, as = 'span', className = '', ...rest }) => {
  const Tag = as;
  return (
    <Tag translate="no" className={`notranslate ${className}`.trim()} {...rest}>
      {children}
    </Tag>
  );
};
